import { FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { getApiErrorMessage, requestOtp } from '../../api/client';
import BrandMark from '../../components/BrandMark';
import ThemeToggle from '../../components/ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';
import { consumeSsoError } from '../../utils/embed';

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() =>
    consumeSsoError() ? 'SSO sign-in failed. Ask an admin to provision your account, or sign in with OTP.' : '',
  );
  const [info, setInfo] = useState('');

  if (user) {
    return <Navigate to="/tickets" replace />;
  }

  async function handleRequestOtp(e: FormEvent) {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await requestOtp(email.trim().toLowerCase());
      setStep('otp');
      setInfo('If this account exists, a one-time code was sent to your email.');
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), code.trim());
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-page-inner">
        <div className="login-page-header">
          <ThemeToggle compact />
        </div>
        <div className="card login-card">
        <div className="card-body">
          <div className="login-brand">
            <BrandMark variant="full" />
          </div>
          <h1>Sign in</h1>
          <p>Staff access via one-time password</p>

          {error && <div className="alert alert-error">{error}</div>}
          {info && <div className="alert alert-info">{info}</div>}

          {step === 'email' ? (
            <form onSubmit={handleRequestOtp}>
              <div className="form-group">
                <label htmlFor="email">Work email</label>
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sending…' : 'Send OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <div className="form-group">
                <label htmlFor="code">One-time code</label>
                <input
                  id="code"
                  type="text"
                  className="form-control"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Verifying…' : 'Verify & sign in'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep('email')}
                >
                  Back
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
